import {
	ProductItem,
	ProductModel,
	ProductOptions,
	ProductKey,
	ToothArea,
	ToothAreaCombined,
	PROFESSIONAL_PRODUCT_KEYS,
	PROFESSIONAL_PRODUCT_DATA_KEYS,
	PROFESSIONAL_PRODUCT_SCHIENE_KEYS,
	DataTypeForTransferMethod,
	DataTransferType,
	FUNCTIONAL_PRODUCT_KEYS,
	MEMOTAIN_PRODUCT_KEYS,
	RETENTIONSSCHIENE_PRODUCT_DATA_KEYS,
	PEEK_RETAINER_PRODUCT_KEYS,
} from "../models";
import {
	SapJaws,
	SapProductCategorization,
	SapProductCategory,
	SapProductDataTransfer,
	SapProductVariant,
} from "libs/sap/src/lib/models/sap.model";
import { DataTransferMethod } from "libs/treatments/src/lib/models/treatment.model";

export class ProductUtilitiesService
{
	public static getItems(productModel: ProductModel, dataTransfer: DataTransferMethod, dentalToothAreaRangeCombined: ToothAreaCombined): ProductItem[] {
		if (productModel.hasBundleConfigs) {
			const sapJaws = this.getSapJaws(dentalToothAreaRangeCombined);
			const productConfig = this.getProductConfig(productModel, dataTransfer, sapJaws);
			return productConfig.items;
		} else {
			const items = [];
			if (dentalToothAreaRangeCombined.UJ) {
				const ujItems = this.getProductConfig(productModel, dataTransfer, SapJaws.UJ, dentalToothAreaRangeCombined.UJ).items;
				items.push(...ujItems);
			}
			if (dentalToothAreaRangeCombined.LJ) {
				const ljItems = this.getProductConfig(productModel, dataTransfer, SapJaws.LJ, dentalToothAreaRangeCombined.LJ).items;
				items.push(...ljItems);
			}
			return items;
		}
	}

	public static getItemCodes(productModel: ProductModel, dataTransfer: DataTransferMethod, dentalToothAreaRangeCombined: ToothAreaCombined): string[] {
		return this.getItems(productModel, dataTransfer, dentalToothAreaRangeCombined).map((item: ProductItem) => item.itemCode);
	}

	public static getDataTransferMethod(dataTransfer: DataTransferMethod): DataTransferType {
		return DataTypeForTransferMethod[dataTransfer];
	}

    private static getProductConfig(productModel: ProductModel, dataTransfer: DataTransferMethod, jaws: SapJaws, dentalToothAreaRange?: ToothArea)
    {
		const method = this.getDataTransferMethod(dataTransfer);
		const qualifiedProductConfigs = productModel.productConfigs.filter(productConfig => (
			productConfig.jaws === jaws &&
			productConfig.method === method &&
			(!dentalToothAreaRange || productConfig.range === dentalToothAreaRange)
		));

        if(qualifiedProductConfigs.length === 1)
        {
            return qualifiedProductConfigs[0];
        }

        throw new Error(`Invalid amount of product configs identified using treatment parameters: ${qualifiedProductConfigs.length} Permitted: 1`);
    }

    public static getSapJaws(toothAreaCombined: ToothAreaCombined): SapJaws
    {
		const isUj = toothAreaCombined.UJ !== ToothArea.NONE;
		const isLj = toothAreaCombined.LJ !== ToothArea.NONE;

		if(isUj && isLj) {
			return SapJaws.BOTH;
		} else if(isUj) {
			return SapJaws.UJ;
		} else if(isLj) {
			return SapJaws.LJ;
		}

		return SapJaws.NONE;
	}

    public static getDentalRange(productOptions: ProductOptions): ToothAreaCombined
    {
        const ujRange = productOptions.uj ?? ToothArea.NONE;
        const ljRange = productOptions.lj ?? ToothArea.NONE;
        return { UJ: ujRange, LJ: ljRange };
    }

    public static getProductCategorization(productKey: ProductKey, productOptions: ProductOptions): SapProductCategorization
    {
        let category: SapProductCategory = SapProductCategory.NONE;
        let variant: SapProductVariant = SapProductVariant.NONE;
		const dataTransfer: SapProductDataTransfer = productOptions.dataTransfer === DataTransferMethod.UPLOAD
			? SapProductDataTransfer.DATA_UPLOAD
			: SapProductDataTransfer.MODELS;

        if(PROFESSIONAL_PRODUCT_KEYS.includes(productKey))
        {
            category = SapProductCategory.PROFESSIONAL;
            if(PROFESSIONAL_PRODUCT_DATA_KEYS.includes(productKey))
            {
                variant = SapProductVariant.DATA;
            }
            else if(PROFESSIONAL_PRODUCT_SCHIENE_KEYS.includes(productKey))
            {
                variant = SapProductVariant.SPLINT;
            }
        }
        else if(MEMOTAIN_PRODUCT_KEYS.includes(productKey))
        {
            category = SapProductCategory.MEMOTAIN;
			if(productKey === ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE) {
				category = SapProductCategory.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE;
			}
        }
		else if (RETENTIONSSCHIENE_PRODUCT_DATA_KEYS.includes(productKey)) {
			category = SapProductCategory.RETENTIONSSCHIENE;
		}
		else if ([...FUNCTIONAL_PRODUCT_KEYS, ProductKey.FUNCTIONAL_SCHIENE_ASSESSMENT].includes(productKey)) {
			category = SapProductCategory.FUNCTIONAL;
		}
		else if (productKey === ProductKey.MICHIGAN_SPLINT) {
			category = SapProductCategory.MICHIGAN_SPLINT;
		}
		else if (PEEK_RETAINER_PRODUCT_KEYS.includes(productKey)) {
			category = SapProductCategory.PEEK_RETAINER;
			if(productKey === ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE) {
				category = SapProductCategory.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE;
			}
		}

        return { category: category, variant: variant, dataTransfer: dataTransfer };
    }

	public static getMemotainClearBundleItems(
		memotainClearProductModel: ProductModel,
		dataTransfer: DataTransferMethod,
		toothAreaCombined: ToothAreaCombined
	): ProductItem[] {
		const dentalToothAreaRangeCombined: ToothAreaCombined = {
			UJ: toothAreaCombined.UJ !== ToothArea.NONE ? ToothArea.SEVEN_SEVEN : ToothArea.NONE,
			LJ: toothAreaCombined.LJ !== ToothArea.NONE ? ToothArea.SEVEN_SEVEN : ToothArea.NONE,
		};
		return this.getItems(memotainClearProductModel, dataTransfer, dentalToothAreaRangeCombined);
	}

	public static getBaseProductKeyOfRetainerBundleProduct(productKey: ProductKey): ProductKey {
		if (productKey === ProductKey.MEMOTAIN_RETENTIONSSCHIENE_BUNDLE) {
			return ProductKey.MEMOTAIN;
		}
		if (productKey === ProductKey.PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE) {
			return ProductKey.PEEK_RETAINER;
		}
		return productKey;
	}
}
